import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from './Toast';
import { categoryType } from './CategoryPicker';
import { Icon, categoryIconName } from './Icon';
import { theme } from '../theme';
import { todayIso } from '../utils/format';
import { ApiError } from '../api/types';
import { celebrateReward, rewardForOp } from '../utils/coins';
import { CategoryCreateForm } from './CategoryCreateForm';

function shiftIso(iso: string, days: number): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Быстрый ввод операции — bottom-sheet с живой суммой, numpad и категориями. */
export function AddSheet({ onClose }: { onClose: () => void }) {
  const { viewData: data, addTx } = useBudget();
  const toast = useToast();

  const today = todayIso();
  const [date, setDate] = useState(today);
  const [category, setCategory] = useState<string | null>(null);
  const [amountStr, setAmountStr] = useState('');
  const [allCats, setAllCats] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const amount = Number(amountStr.replace(',', '.'));
  const valid = !!category && Number.isFinite(amount) && amount > 0 && !busy;
  const type = data && category ? categoryType(category, data.categories) : null;
  const currency = data?.settings.currency ?? '₽';

  const frequent = useMemo(() => {
    if (!data) return [];
    const cut = shiftIso(today, -60);
    const count = new Map<string, number>();
    for (const op of data.operations) {
      if (op.date < cut) continue;
      count.set(op.category, (count.get(op.category) || 0) + 1);
    }
    return [...data.categories]
      .sort((a, b) => (count.get(b.name) || 0) - (count.get(a.name) || 0) || a.sort_order - b.sort_order)
      .slice(0, 5);
  }, [data, today]);

  const allSorted = useMemo(
    () => [...(data?.categories ?? [])].sort((a, b) => {
      if (a.type !== b.type) return a.type === 'Расход' ? -1 : 1;
      return a.sort_order - b.sort_order;
    }),
    [data],
  );

  function press(key: string) {
    setAmountStr((s) => {
      if (key === 'del') return s.slice(0, -1);
      if (key === '.') return s.includes('.') ? s : (s || '0') + '.';
      if (s.replace('.', '').length >= 9) return s;
      if (s === '0') return key;
      return s + key;
    });
  }

  async function submit() {
    if (!valid || !category) return;
    setBusy(true);
    try {
      const r = await addTx({ date, category, amount });
      // награда детерминирована id — праздновать офлайн безопасно (запись доедет)
      celebrateReward(rewardForOp({ id: r.id, type: type ?? 'Расход' }), toast, {
        title: category ?? undefined,
        sub: r.synced ? undefined : 'синхронизируется позже',
      });
      onClose();
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка сохранения', 'error');
    } finally {
      setBusy(false);
    }
  }

  const amountColor = type === 'Доход' ? theme.colors.green : type === 'Расход' ? theme.colors.accent : theme.colors.ink;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(20,19,15,.4)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.colors.bg,
          borderRadius: '16px 16px 0 0',
          maxWidth: theme.maxWidth,
          margin: '0 auto',
          width: '100%',
          padding: '10px 20px calc(18px + env(safe-area-inset-bottom))',
          animation: 'sheetUp .3s cubic-bezier(.2,.7,.2,1) both',
          boxShadow: '0 -8px 40px rgba(20,19,15,.18)',
        }}
      >
        <style>{`@keyframes sheetUp { from { transform: translateY(100%) } to { transform: none } }`}</style>
        <div style={{ width: 36, height: 4, background: theme.colors.line, borderRadius: 2, margin: '0 auto 12px' }} />

        {/* шапка: подпись + живая сумма */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <span className="caption">{category ?? 'выбери категорию'}</span>
          <span className="num" style={{ fontSize: 40, color: amountStr ? amountColor : theme.colors.line }}>
            {amountStr || '0'}<span style={{ fontSize: '0.4em', color: theme.colors.sub }}> {currency}</span>
          </span>
        </div>

        {/* категории: частые чипы + «ещё» → сетка */}
        {creating ? (
          <div style={{ marginBottom: 14 }}>
            <CategoryCreateForm
              onCancel={() => setCreating(false)}
              onCreated={(name) => { setCategory(name); setCreating(false); }}
            />
          </div>
        ) : !allCats ? (
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 14 }}>
            {frequent.map((c) => {
              const on = category === c.name;
              return (
                <button key={c.name} onClick={() => setCategory(c.name)} style={chip(on)}>
                  <Icon name={categoryIconName(c)} size={14} color={on ? '#E4E1D8' : theme.colors.ink} /> {c.name}
                </button>
              );
            })}
            <button onClick={() => setAllCats(true)} style={chipGhost}>ещё…</button>
            <button onClick={() => setCreating(true)} style={chipGhost}>+ новая категория</button>
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span className="caption">все категории</span>
              <button onClick={() => setAllCats(false)} style={{ ...chipGhost, padding: '4px 10px' }}>← клавиатура</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
              {allSorted.map((c) => {
                const on = category === c.name;
                return (
                  <button
                    key={c.name}
                    onClick={() => { setCategory(c.name); setAllCats(false); }}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      padding: '10px 2px', fontSize: 10, lineHeight: 1.1, textAlign: 'center',
                      background: on ? theme.colors.ink : 'transparent', color: on ? '#E4E1D8' : theme.colors.ink,
                      border: `1px solid ${on ? theme.colors.ink : theme.colors.line}`, borderRadius: theme.radius.sm,
                      fontFamily: theme.font, cursor: 'pointer',
                    }}
                  >
                    <Icon name={categoryIconName(c)} size={18} color={on ? '#E4E1D8' : theme.colors.ink} />
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* дата + numpad + кнопка — скрыты пока открыта форма создания */}
        {!creating && (
          <>
            {/* дата */}
            <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
              {([['t', 'Сегодня', today], ['y', 'Вчера', shiftIso(today, -1)]] as const).map(([id, label, d]) => (
                <button key={id} onClick={() => setDate(d)} style={pill(date === d)}>{label}</button>
              ))}
              <label style={pill(date !== today && date !== shiftIso(today, -1))}>
                {date !== today && date !== shiftIso(today, -1) ? date.slice(8, 10) + '.' + date.slice(5, 7) : 'дата'}
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
              </label>
            </div>

            {/* numpad (скрыт когда открыта сетка категорий) */}
            {!allCats && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'del'].map((k) => (
                  <button key={k} onClick={() => press(k)} style={padBtn}>
                    {k === 'del' ? <Icon name="close" size={18} /> : k}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={submit}
              disabled={!valid}
              style={{
                width: '100%', padding: '15px 0',
                background: valid ? theme.colors.ink : theme.colors.line,
                color: valid ? '#E4E1D8' : theme.colors.sub,
                border: 'none', borderRadius: theme.radius.sm,
                fontFamily: theme.fontDisplay, fontSize: 18, cursor: valid ? 'pointer' : 'not-allowed',
              }}
            >
              {busy ? 'Сохраняю…' : 'Добавить'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const padBtn: CSSProperties = {
  padding: '15px 0', background: 'transparent', border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm, fontFamily: theme.fontDisplay, fontSize: 24, color: theme.colors.ink,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
function chip(on: boolean): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px',
    background: on ? theme.colors.ink : 'transparent', color: on ? '#E4E1D8' : theme.colors.ink,
    border: `1px solid ${on ? theme.colors.ink : theme.colors.line}`, borderRadius: 999,
    fontFamily: theme.font, fontSize: 14, cursor: 'pointer',
  };
}
const chipGhost: CSSProperties = {
  padding: '8px 13px', background: 'transparent', border: `1px dashed ${theme.colors.line}`,
  borderRadius: 999, color: theme.colors.sub, fontFamily: theme.font, fontSize: 13, cursor: 'pointer',
};
function pill(on: boolean): CSSProperties {
  return {
    position: 'relative', padding: '7px 14px',
    background: on ? theme.colors.ink : 'transparent', color: on ? '#E4E1D8' : theme.colors.sub,
    border: `1px solid ${on ? theme.colors.ink : theme.colors.line}`, borderRadius: 999,
    fontFamily: theme.font, fontSize: 13, cursor: 'pointer',
  };
}
